/**
 * DateFilterPopover.jsx
 *
 * Period-aware filter for date columns. Drop-in cousin of ColumnFilterPopover:
 * positions itself by walking up to the column's <th>, supports the same
 * close-on-outside-click / Escape / reposition-on-scroll behavior (all
 * provided by FilterPopoverShell).
 *
 * Filter shape (controlled by parent):
 *   { from?: 'YYYY-MM-DD', to?: 'YYYY-MM-DD' }   — or null when no filter
 *
 * Quick presets are CALENDAR periods before today, in the browser's local
 * time (Sharif, 10/9/26: "last week" is last week, not the last 7 days):
 *   Last Week       the previous Monday - Sunday
 *   Last Month      the previous calendar month
 *   Last 3/6 Months the 3 / 6 full calendar months before this one
 *   Last Year       last calendar year, Jan 1 - Dec 31
 *   This Year       Jan 1 - today
 */
import { useState } from 'react';
import { clsx } from 'clsx';
import { FilterPopoverShell } from './FilterPopoverShell';
import { localDay as ymd } from '../../utils/localDay';

const pad = (n) => String(n).padStart(2, '0');

/** { from, to } ('YYYY-MM-DD', inclusive) of a preset, as of `today`. */
export function presetRange(key, today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  const months = (n) => ({ from: ymd(new Date(y, m - n, 1)), to: ymd(new Date(y, m, 0)) });
  switch (key) {
    case 'lastWeek': {
      const sinceMonday = (today.getDay() + 6) % 7;
      return { from: ymd(new Date(y, m, d - sinceMonday - 7)), to: ymd(new Date(y, m, d - sinceMonday - 1)) };
    }
    case 'lastMonth':
      return months(1);
    case 'last3Months':
      return months(3);
    case 'last6Months':
      return months(6);
    case 'lastYear':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'thisYear':
      return { from: `${y}-01-01`, to: ymd(today) };
    default:
      return { from: '', to: '' };
  }
}

/**
 * A stored date as 'YYYY-MM-DD' for the range compare: ISO text (or a
 * timestamp starting with it) as is, and the older 'M/D/YYYY' the imported
 * rows carry (2/14/2024). Anything else -> ''.
 */
export function dayKey(v) {
  const s = String(v == null ? '' : v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  return us ? `${us[3]}-${pad(us[1])}-${pad(us[2])}` : '';
}

const PRESETS = [
  { id: 'lastWeek', label: 'Last Week' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'last3Months', label: 'Last 3 Months' },
  { id: 'last6Months', label: 'Last 6 Months' },
  { id: 'lastYear', label: 'Last Year' },
  { id: 'thisYear', label: 'This Year' },
];

export function DateFilterPopover({ columnLabel, selected, onApply, onSort, sortDir, onClose }) {
  const [from, setFrom] = useState(selected?.from || '');
  const [to, setTo] = useState(selected?.to || '');
  const [activePreset, setActivePreset] = useState(null);

  const pick = (id) => {
    const r = presetRange(id);
    setFrom(r.from);
    setTo(r.to);
    setActivePreset(id);
  };

  const apply = () => {
    if (!from && !to) {
      onApply?.(null);
      onClose?.();
      return;
    }
    // Swap if the user picked them in the wrong order — friendlier than erroring.
    let lo = from || null;
    let hi = to || null;
    if (lo && hi && lo > hi) [lo, hi] = [hi, lo];
    onApply?.({ from: lo, to: hi });
    onClose?.();
  };
  const clear = () => {
    onApply?.(null);
    onClose?.();
  };

  return (
    <FilterPopoverShell
      width={320}
      sortAscLabel="Sort oldest → newest"
      sortDescLabel="Sort newest → oldest"
      sortDir={sortDir}
      onSort={onSort}
      onClose={onClose}
      onClear={clear}
      onApply={apply}
    >
      {/* Presets */}
      <div className="px-3 py-2 border-b border-black/5">
        <div className="text-[10px] uppercase font-bold tracking-[1.2px] text-text-muted mb-1.5">Quick periods</div>
        <div className="grid grid-cols-2 gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pick(p.id)}
              className={clsx(
                'px-2 py-1.5 rounded text-[11px] font-bold border',
                activePreset === p.id
                  ? 'bg-gat-green text-white border-gat-greenDarkest'
                  : 'bg-surface-2 text-text-body border-transparent hover:bg-gat-green/10 hover:text-gat-greenDark'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom range */}
      <div className="px-3 py-2 border-b border-black/5">
        <div className="text-[10px] uppercase font-bold tracking-[1.2px] text-text-muted mb-1.5">Custom range</div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setActivePreset(null);
            }}
            className="flex-1 bg-surface-3 border border-transparent rounded px-2 py-1 text-[11.5px] focus:outline-none focus:ring-2 focus:ring-gat-green/30"
          />
          <span className="text-[10px] text-text-muted">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setActivePreset(null);
            }}
            className="flex-1 bg-surface-3 border border-transparent rounded px-2 py-1 text-[11.5px] focus:outline-none focus:ring-2 focus:ring-gat-green/30"
          />
        </div>
      </div>
    </FilterPopoverShell>
  );
}
